/**
 * CLOUD STORE
 *
 * Zustand store for managing cloud resources and costs per project.
 */

import { create } from "zustand";
import { api } from "@/lib/api";

export interface CloudResource {
  id: number;
  projectId: number;
  resourceArn: string;
  resourceType: string;
  resourceName: string;
  region: string;
  discoveryMethod: string;
  awsTags: Record<string, string>;
  costDaily: number;
  costMonthly: number;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface CloudCostSummary {
  totalDaily: number;
  totalMonthly: number;
  costByService: Record<string, number>;
  periodStart?: string;
  periodEnd?: string;
  refreshedAt?: string;
}

export interface CloudDiscoveryResult {
  discovered: number;
  added: number;
  skipped: number;
}

function mapResourceFromApi(r: any): CloudResource {
  return {
    id: r.id,
    projectId: r.project_id,
    resourceArn: r.resource_arn,
    resourceType: r.resource_type,
    resourceName: r.resource_name,
    region: r.region,
    discoveryMethod: r.discovery_method,
    awsTags: r.aws_tags || {},
    costDaily: r.cost_daily || 0,
    costMonthly: r.cost_monthly || 0,
    status: r.status,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  };
}

function mapCostFromApi(c: any): CloudCostSummary {
  return {
    totalDaily: c.total_daily || 0,
    totalMonthly: c.total_monthly || 0,
    costByService: c.cost_by_service || {},
    periodStart: c.period_start,
    periodEnd: c.period_end,
    refreshedAt: c.refreshed_at,
  };
}

interface CloudState {
  resources: Record<number, CloudResource[]>;
  costs: Record<number, CloudCostSummary>;
  loading: Record<number, boolean>;
  discovering: Record<number, boolean>;
  refreshingCosts: Record<number, boolean>;

  fetchResources: (projectId: number) => Promise<void>;
  addResource: (
    projectId: number,
    data: { resource_arn: string; resource_type: string; resource_name: string; region: string }
  ) => Promise<CloudResource>;
  removeResource: (resourceId: number, projectId: number) => Promise<void>;
  discoverResources: (projectId: number) => Promise<CloudDiscoveryResult>;
  fetchCosts: (projectId: number) => Promise<void>;
  refreshCosts: (projectId: number) => Promise<CloudCostSummary>;
}

export const useCloudStore = create<CloudState>()((set, get) => ({
  resources: {},
  costs: {},
  loading: {},
  discovering: {},
  refreshingCosts: {},

  fetchResources: async (projectId: number) => {
    set((s) => ({ loading: { ...s.loading, [projectId]: true } }));
    try {
      const data = await api.get<any[]>(`/api/projects/${projectId}/cloud-resources`);
      const resources = data.map(mapResourceFromApi);
      set((s) => ({
        resources: { ...s.resources, [projectId]: resources },
        loading: { ...s.loading, [projectId]: false },
      }));
    } catch (e) {
      console.error("Failed to fetch cloud resources:", e);
      set((s) => ({ loading: { ...s.loading, [projectId]: false } }));
    }
  },

  addResource: async (projectId, data) => {
    const res = await api.post<any>(`/api/projects/${projectId}/cloud-resources`, data);
    const resource = mapResourceFromApi(res);
    set((s) => {
      const existing = s.resources[projectId] || [];
      const idx = existing.findIndex((r) => r.id === resource.id);
      const updated = idx >= 0
        ? existing.map((r) => (r.id === resource.id ? resource : r))
        : [...existing, resource];
      return { resources: { ...s.resources, [projectId]: updated } };
    });
    return resource;
  },

  removeResource: async (resourceId, projectId) => {
    await api.delete(`/api/cloud-resources/${resourceId}`);
    set((s) => ({
      resources: {
        ...s.resources,
        [projectId]: (s.resources[projectId] || []).filter((r) => r.id !== resourceId),
      },
    }));
  },

  discoverResources: async (projectId) => {
    set((s) => ({ discovering: { ...s.discovering, [projectId]: true } }));
    try {
      const result = await api.post<CloudDiscoveryResult>(
        `/api/projects/${projectId}/cloud-discover`
      );
      // Re-fetch resources to get the updated list
      await get().fetchResources(projectId);
      set((s) => ({ discovering: { ...s.discovering, [projectId]: false } }));
      return result;
    } catch (e) {
      set((s) => ({ discovering: { ...s.discovering, [projectId]: false } }));
      throw e;
    }
  },

  fetchCosts: async (projectId) => {
    try {
      const data = await api.get<any>(`/api/projects/${projectId}/cloud-costs`);
      const costs = mapCostFromApi(data);
      set((s) => ({ costs: { ...s.costs, [projectId]: costs } }));
    } catch (e) {
      console.error("Failed to fetch cloud costs:", e);
    }
  },

  refreshCosts: async (projectId) => {
    set((s) => ({ refreshingCosts: { ...s.refreshingCosts, [projectId]: true } }));
    try {
      const data = await api.post<any>(`/api/projects/${projectId}/cloud-costs/refresh`);
      const costs = mapCostFromApi(data);
      set((s) => ({
        costs: { ...s.costs, [projectId]: costs },
        refreshingCosts: { ...s.refreshingCosts, [projectId]: false },
      }));
      return costs;
    } catch (e) {
      set((s) => ({ refreshingCosts: { ...s.refreshingCosts, [projectId]: false } }));
      throw e;
    }
  },
}));
